import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material';
import { useCart } from '../context/CartContext';
import { orderApi, walletApi } from '../services/api';

const requiredFields = [
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'address', label: 'Address' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'postalCode', label: 'Pincode' }
];

const loadRazorpayScript = () => {
  if (window.Razorpay) {
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const Checkout = () => {
  const navigate = useNavigate();
  const { cart, getTotalPrice, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [loadingWallet, setLoadingWallet] = useState(true);
  const [walletBalance, setWalletBalance] = useState(0);
  const [useWallet, setUseWallet] = useState(false);
  const [walletAmount, setWalletAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('razorpay');
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState({});
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'India'
  });

  React.useEffect(() => {
    (async () => {
      try {
        const res = await walletApi.getBalance();
        setWalletBalance(Number(res.data.balance || 0));
      } catch {
        setWalletBalance(0);
      } finally {
        setLoadingWallet(false);
      }
    })();
  }, []);

  const subtotal = Number(getTotalPrice() || 0);
  const maxWalletAllowed = Math.min(100, walletBalance, subtotal);
  const normalizedWalletAmount = useWallet ? Math.min(Math.max(walletAmount, 0), maxWalletAllowed) : 0;
  const deliveryBase = Math.max(subtotal - normalizedWalletAmount, 0);
  const deliveryCharge = deliveryBase > 200 ? 0 : 40;
  const payable = Math.max(subtotal - normalizedWalletAmount + deliveryCharge, 0);

  const validationErrors = useMemo(() => {
    const errors = {};

    requiredFields.forEach(({ key, label }) => {
      if (!String(formData[key] || '').trim()) {
        errors[key] = `${label} is required`;
      }
    });

    if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email.trim())) {
      errors.email = 'Enter a valid email';
    }

    const phoneDigits = String(formData.phone || '').replace(/\D/g, '');
    if (formData.phone && phoneDigits.length < 10) {
      errors.phone = 'Enter a valid phone number';
    }

    const pinDigits = String(formData.postalCode || '').replace(/\D/g, '');
    if (formData.postalCode && pinDigits.length !== 6) {
      errors.postalCode = 'Enter a valid 6-digit pincode';
    }

    return errors;
  }, [formData]);

  const isFormValid = Object.keys(validationErrors).length === 0;
  const canPlaceOrder = isFormValid && cart.length > 0 && subtotal > 0 && !loading && !loadingWallet;

  const shouldShowError = (field) => Boolean((submitted || touched[field]) && validationErrors[field]);

  const handleFieldChange = (key, value) => {
    const formattedValue =
      key === 'phone'
        ? value.replace(/[^\d+ -]/g, '').slice(0, 15)
        : key === 'postalCode'
        ? value.replace(/\D/g, '').slice(0, 6)
        : value;

    setFormData((previous) => ({
      ...previous,
      [key]: formattedValue
    }));
  };

  const getOrderData = (paymentOverrides = {}) => ({
    items: cart,
    subtotalAmount: subtotal,
    totalAmount: payable,
    shippingAddress: { ...formData },
    email: formData.email,
    phone: formData.phone,
    paymentMethod,
    walletUsed: useWallet,
    walletAmount: normalizedWalletAmount,
    amountToPayByCard: payable,
    deliveryCharge,
    ...paymentOverrides
  });

  const createLocalOrder = async (orderData) => {
    const response = await orderApi.create(orderData);
    await clearCart();
    navigate(`/order-confirmation/${response.data.order._id}`);
  };

  const processRazorpayPayment = async (orderData) => {
    if (payable <= 0) {
      await createLocalOrder({
        ...orderData,
        paymentMethod: 'razorpay'
      });
      return;
    }

    const scriptLoaded = await loadRazorpayScript();
    if (!scriptLoaded) {
      throw new Error('Unable to load Razorpay. Please check your internet connection.');
    }

    const razorpayOrderRes = await orderApi.createRazorpayOrder({
      amount: payable,
      currency: 'INR'
    });

    const razorpayOrder = razorpayOrderRes.data.order;
    const keyId = razorpayOrderRes.data.keyId;

    await new Promise((resolve, reject) => {
      const razorpay = new window.Razorpay({
        key: keyId,
        amount: razorpayOrder.amount,
        currency: razorpayOrder.currency,
        name: 'SwadVeda Masale',
        description: 'Order payment',
        order_id: razorpayOrder.id,
        prefill: {
          name: `${formData.firstName} ${formData.lastName}`.trim(),
          email: formData.email,
          contact: formData.phone
        },
        notes: {
          city: formData.city,
          pincode: formData.postalCode
        },
        theme: {
          color: '#8B0000'
        },
        handler: async (response) => {
          try {
            await orderApi.verifyRazorpayPayment(response);
            await createLocalOrder({
              ...orderData,
              paymentMethod: 'razorpay',
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature
            });
            resolve();
          } catch (err) {
            reject(err);
          }
        },
        modal: {
          ondismiss: () => {
            reject(new Error('Payment was cancelled.'));
          }
        }
      });

      razorpay.open();
    });
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setError('');

    if (!isFormValid) {
      setError('Please complete all required shipping details.');
      return;
    }

    if (cart.length === 0) {
      setError('Your cart is empty.');
      return;
    }

    if (subtotal <= 0) {
      setError('Order total is invalid.');
      return;
    }

    setLoading(true);

    try {
      const orderData = getOrderData();

      if (paymentMethod === 'razorpay') {
        await processRazorpayPayment(orderData);
      } else {
        await createLocalOrder(orderData);
      }
    } catch (err) {
      console.error('Order error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to place order.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1180, mx: 'auto', px: { xs: 1.5, md: 2.5 }, py: { xs: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, md: 2.5 }, mb: 2, borderRadius: 3, background: 'linear-gradient(135deg,#fff7f3,#fff)', border: '1px solid rgba(139,0,0,0.12)' }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: '#8B0000' }}>Secure Checkout</Typography>
        <Typography variant="body2" color="text.secondary">Pay online with Razorpay or choose Cash on Delivery.</Typography>
      </Paper>

      <Grid component="form" onSubmit={handleSubmitOrder} container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3 }}>
            <Typography sx={{ fontWeight: 700, mb: 1.5, color: '#5c0000' }}>Shipping Details</Typography>
            <Grid container spacing={1.5}>
              {requiredFields.map(({ key, label }) => (
                <Grid item xs={12} sm={key === 'address' ? 12 : 6} key={key}>
                  <TextField
                    fullWidth
                    size="small"
                    label={label}
                    value={formData[key]}
                    onChange={(e) => handleFieldChange(key, e.target.value)}
                    onBlur={() => setTouched((previous) => ({ ...previous, [key]: true }))}
                    required
                    error={shouldShowError(key)}
                    helperText={shouldShowError(key) ? validationErrors[key] : ' '}
                    type={key === 'email' ? 'email' : 'text'}
                    inputMode={key === 'phone' || key === 'postalCode' ? 'numeric' : undefined}
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3, position: 'sticky', top: 10 }}>
            <Typography sx={{ fontWeight: 700, mb: 1.2 }}>Order Summary</Typography>

            <Paper variant="outlined" sx={{ p: 1.2, mb: 1.2, borderColor: '#e8d6d1', background: '#fff9f7' }}>
              <Typography sx={{ fontSize: 13, color: '#8B0000', fontWeight: 700 }}>Wallet Balance</Typography>
              <Typography sx={{ fontWeight: 800, fontSize: 20 }}>Rs.{loadingWallet ? '...' : walletBalance.toFixed(2)}</Typography>
            </Paper>

            <Box display="flex" justifyContent="space-between"><Typography>Subtotal</Typography><Typography>Rs.{subtotal.toFixed(2)}</Typography></Box>
            <Box display="flex" justifyContent="space-between"><Typography>Tax</Typography><Typography>Rs.0.00</Typography></Box>
            <Box display="flex" justifyContent="space-between"><Typography>Delivery</Typography><Typography>{deliveryCharge === 0 ? 'Free' : `Rs.${deliveryCharge.toFixed(2)}`}</Typography></Box>
            <Typography variant="caption" color="text.secondary">Free delivery above Rs.200 after wallet deduction.</Typography>
            <Divider sx={{ my: 1.2 }} />

            <FormControlLabel
              control={
                <Checkbox
                  checked={useWallet}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setUseWallet(checked);
                    setWalletAmount(checked ? maxWalletAllowed : 0);
                  }}
                />
              }
              label={`Use Wallet (Max Rs.${maxWalletAllowed.toFixed(0)})`}
            />
            {useWallet && (
              <TextField
                size="small"
                type="number"
                fullWidth
                label="Wallet amount"
                value={walletAmount}
                onChange={(e) => setWalletAmount(Math.max(0, Math.min(Number(e.target.value || 0), maxWalletAllowed)))}
                helperText={`Allowed up to Rs.${maxWalletAllowed.toFixed(2)}`}
              />
            )}

            <Divider sx={{ my: 1.2 }} />
            <FormControl fullWidth>
              <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Payment Method</Typography>
              <RadioGroup value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                <FormControlLabel value="razorpay" control={<Radio />} label="Pay Online (Razorpay)" />
                <FormControlLabel value="cod" control={<Radio />} label="Cash on Delivery" />
              </RadioGroup>
            </FormControl>

            {error && <Alert sx={{ mt: 1 }} severity="error">{error}</Alert>}
            <Divider sx={{ my: 1.2 }} />
            <Box display="flex" justifyContent="space-between" sx={{ mb: 1.2 }}>
              <Typography sx={{ fontWeight: 700 }}>{paymentMethod === 'razorpay' ? 'Pay Online' : 'Payable (COD)'}</Typography>
              <Typography sx={{ fontWeight: 800, color: '#8B0000' }}>Rs.{payable.toFixed(2)}</Typography>
            </Box>
            {normalizedWalletAmount > 0 && (
              <Box display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography color="text.secondary">Wallet Used</Typography>
                <Typography sx={{ color: '#2e7d32', fontWeight: 700 }}>-Rs.{normalizedWalletAmount.toFixed(2)}</Typography>
              </Box>
            )}
            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={!canPlaceOrder}
              sx={{ background: '#8B0000', textTransform: 'none', py: 1.1 }}
            >
              {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : paymentMethod === 'razorpay' ? 'Pay & Place Order' : 'Place COD Order'}
            </Button>
            {!isFormValid && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Complete shipping details to continue.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Checkout;
